import React, { useState, useEffect } from 'react';
import {
  Chip,
  Slider,
  Form,
  Button,
  Input,
  Textarea,
  Spinner,
  Tooltip,
  Badge,
  Checkbox,
  Accordion,
  AccordionItem,
  Popover,
  PopoverTrigger,
  PopoverContent
} from "@heroui/react";
import CampaignPreview from './CampaignPreview';
import { getCookie, setCookie } from '../utils/cookieUtils';
import { validateEmails, validateEmailsStandalone, getValidationColor, getValidationStatus, needsValidation } from '../utils/emailValidationApi';

const CampaignForm = ({cancelForm, user, campaign, stompClient, setBanner}) => {
  const [mailerSubject, setMailerSubject] = useState(campaign?.subject || '');
  const [description, setDescription] = useState(campaign?.description || '');
  const [recipients, setRecipients] = useState(campaign?.recipients || []);
  const [toRecipients, setToRecipients] = useState(campaign?.to_recipients || []);
  const [ccRecipients, setCcRecipients] = useState(campaign?.cc_recipients || []);
  const [recipient, setRecipient] = useState();
  const [toRecipient, setToRecipient] = useState();
  const [ccRecipient, setCcRecipient] = useState();
  const [emailValidations, setEmailValidations] = useState(campaign?.email_validations || {});
  const [validatingEmails, setValidatingEmails] = useState(false);
  const [mailerContent, setMailerContent] = useState(campaign?.mailer_content || '');
  const [vanityUrl, setVanityUrl] = useState(campaign?.vanity_url);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState();
  const [uriErrors, setUriErrors] = useState();
  const [bodyError, setBodyError] = useState();
  const [subjectError, setSubjectError] = useState();
  const [showFooter, setShowFooter] = useState(campaign ? campaign.include_footer : true);
  const [typos, setTypos] = useState(campaign ? campaign.typos : true);
  const [mpLookup, setMpLookup] = useState(campaign ? campaign.mp_lookup : false);
  const [requireSenderAddress, setRequireSenderAddress] = useState(campaign ? campaign.require_sender_address : false);
  const [variability, setVariability] = useState(campaign ? (campaign?.variability * 10) : 5);
  const [subjectVariability, setSubjectVariability] = useState(campaign ? (campaign?.subject_variability * 10) : 5);
  const [tone, setTone] = useState(campaign ? campaign.tone : 5);
  const [fetchingCompliance, setFetchingCompliance] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [promptInject, setPromptInject] = useState(campaign?.prompt_inject || '');
  const [tipJarMessage, setTipJarMessage] = useState(campaign?.tip_jar_message || '');
  const [useRecipientSubset, setUseRecipientSubset] = useState(campaign?.use_recipient_subset || false);
  const [dragOver, setDragOver] = useState({ to: false, cc: false, bcc: false });
  const [autoCompleteEnabled, setAutoCompleteEnabled] = useState(!!campaign?.auto_complete_at);
  const [autoCompleteAt, setAutoCompleteAt] = useState(campaign?.auto_complete_at ? new Date(campaign.auto_complete_at).toISOString().slice(0, 16) : '');
  const [autoCompleteMessage, setAutoCompleteMessage] = useState(campaign?.auto_complete_message || '');
  const [isInitialTooltipOpen, setIsInitialTooltipOpen] = useState(false);
  const [isDescriptionTooltipOpen, setIsDescriptionTooltipOpen] = useState(false);
  const [isVanityTooltipOpen, setIsVanityTooltipOpen] = useState(false);
  const [isToEmailTooltipOpen, setIsToEmailTooltipOpen] = useState(false);
  const [isCcEmailTooltipOpen, setIsCcEmailTooltipOpen] = useState(false);
  const [isBccEmailTooltipOpen, setIsBccEmailTooltipOpen] = useState(false);
  const [isBodyTooltipOpen, setIsBodyTooltipOpen] = useState(false);
  const [isVariabilityTooltipOpen, setIsVariabilityTooltipOpen] = useState(false);
  const [isSubjectVariabilityTooltipOpen, setIsSubjectVariabilityTooltipOpen] = useState(false);
  const [isToneTooltipOpen, setIsToneTooltipOpen] = useState(false);
  const [advancedSectionExpanded, setAdvancedSectionExpanded] = useState(() => {
    // Initialize from cookie, default to collapsed (empty array) if no cookie exists
    const savedState = getCookie('campaignFormAdvancedExpanded');
    return savedState === 'true' ? ['advanced'] : [];
  });
  const minBodyLength = 50;
  const minSubjectLength = 15;

  // Unicode detection utility function
  const isUnicodeCharacter = (char) => {
    const code = char.charCodeAt(0);
    // Check for common unicode characters that should be replaced
    // This includes smart quotes, em dashes, en dashes, etc.
    return (
      (code >= 0x2000 && code <= 0x206F) || // General Punctuation
      (code >= 0x2070 && code <= 0x209F) || // Superscripts and Subscripts
      (code >= 0x20A0 && code <= 0x20CF) || // Currency Symbols
      (code >= 0x2100 && code <= 0x214F) || // Letterlike Symbols
      (code >= 0x2150 && code <= 0x218F) || // Number Forms
      (code >= 0x2190 && code <= 0x21FF) || // Arrows
      (code >= 0x2200 && code <= 0x22FF) || // Mathematical Operators
      code === 0x2018 || code === 0x2019 || // Left/Right Single Quotation Mark
      code === 0x201C || code === 0x201D || // Left/Right Double Quotation Mark
      code === 0x2013 || code === 0x2014 || // En Dash, Em Dash
      code === 0x2026 || // Horizontal Ellipsis
      code > 0x007F // Any character above basic ASCII
    );
  };

  // Get ASCII replacement suggestion for unicode characters
  const getUnicodeReplacement = (char) => {
    const code = char.charCodeAt(0);
    const replacements = {
      0x2018: "'", // Left Single Quotation Mark
      0x2019: "'", // Right Single Quotation Mark
      0x201C: '"', // Left Double Quotation Mark
      0x201D: '"', // Right Double Quotation Mark
      0x2013: '-', // En Dash
      0x2014: '--', // Em Dash
      0x2026: '...', // Horizontal Ellipsis
      0x00A0: ' ', // Non-breaking space
      0x2010: '-', // Hyphen
      0x2011: '-', // Non-breaking hyphen
      0x2012: '-', // Figure dash
      0x00AB: '"', // Left-pointing double angle quotation mark
      0x00BB: '"', // Right-pointing double angle quotation mark
    };

    return replacements[code] || char;
  };

  // Function to replace all unicode characters in text with ASCII equivalents
  const replaceUnicodeCharacters = (text) => {
    if (!text) return text;

    return text.split('').map(char => {
      if (isUnicodeCharacter(char)) {
        return getUnicodeReplacement(char);
      }
      return char;
    }).join('');
  };

  useEffect(() => {
    if (campaign && campaign.email_validations) {
      // Use stored validation data from the campaign
      setEmailValidations(campaign.email_validations);

      // Check if this is a pre-existing campaign that needs validation
      if (campaign.needs_email_validation && campaign.unvalidated_emails && campaign.unvalidated_emails.length > 0) {
        console.log('Pre-existing campaign needs validation for emails:', campaign.unvalidated_emails);
        setValidatingEmails(true);

        // Validate unvalidated emails in the background
        if (campaign._id) {
          validateEmails(campaign._id, campaign.unvalidated_emails)
            .then(newValidations => {
              console.log('Validation complete for pre-existing campaign:', newValidations);
              setEmailValidations(prev => ({...prev, ...newValidations}));
              setValidatingEmails(false);
            })
            .catch(error => {
              console.error('Error validating pre-existing campaign emails:', error);
              setValidatingEmails(false);
            });
        } else {
          // No campaign ID available, use standalone validation
          validateEmailsStandalone(campaign.unvalidated_emails)
            .then(newValidations => {
              console.log('Validation complete for campaign without ID:', newValidations);
              setEmailValidations(prev => ({...prev, ...newValidations}));
              setValidatingEmails(false);
            })
            .catch(error => {
              console.error('Error validating emails standalone:', error);
              setValidatingEmails(false);
            });
        }
      }
    }
    // eslint-disable-next-line
  }, [campaign]);

  const moveEmail = async (addr, oldSetter, newSetter) => {
    await oldSetter(prev => {return prev.filter(email => email !== addr)});
    await newSetter(prev => {return prev.concat([addr])});
  };

  const getRecipientBorderColor = (recipientList) => {
    if (recipientList.length === 0) return 'none';

    // Check if any recipient has validation issues (red color)
    const hasInvalidEmails = recipientList.some(email => getEmailValidationColor(email) === 'danger');

    return hasInvalidEmails ? '1px solid #ff0000' : '1px solid #009030';
  };

  const isValid = async () => {
    let valid = true;
     if (!vanityUrl) {
       setUriErrors('Must name your cause!');
       valid = false;
     } else if ((campaign && vanityUrl !== campaign.vanity_url) || !campaign) {
       const vanityCheck = await validateUrl(vanityUrl);
       if (!vanityCheck) {
         valid = false;
       }
     }
     let errors = [];
     if (mailerSubject?.length < minSubjectLength) {
       setSubjectError('Subject is too short to be expanded upon.');
       valid = false;
     }
     if (!mpLookup && (!recipients || recipients?.length === 0) && (!toRecipients || toRecipients?.length === 0) && (!ccRecipients || ccRecipients?.length === 0)) {
       errors.push("No recipients specified!");
     }
     if (mailerContent?.length < minBodyLength) {
       setBodyError("Body is too short to be expanded upon.");
       valid = false;
     }
     if (autoCompleteEnabled && !autoCompleteAt) {
       alert("Auto-completion date/time is required when auto-completion is enabled!");
       valid = false;
     }
     if (autoCompleteEnabled && autoCompleteAt && campaign?.completion_text &&
         (!campaign?.auto_complete_at || new Date(campaign.auto_complete_at) <= new Date())) {
       const confirmed = window.confirm("This campaign is already completed. Setting a future auto-completion date will mark it as not-completed and reactivate it. Do you want to continue?");
       if (!confirmed) {
         valid = false;
       }
     }
     if (errors.length > 0) {
       setValidationErrors(errors);
       valid = false;
     }
     return valid;
  };

  const submitCampaign = async (e) => {
    setLoading(true);
    setUriErrors();
    setValidationErrors();
    e.preventDefault();

    const campaignIsValid = await isValid();
    if (!campaignIsValid) {
      setLoading(false);
      return;
    }

    // Check recipient count for new campaigns
    const totalRecipients = recipients.length + toRecipients.length + ccRecipients.length;
    const isUpdate = !!campaign?._id;

    if (!isUpdate && totalRecipients >= 100) {
      // Force sampling for 100+ recipients
      setUseRecipientSubset(true);
      await performSubmit(true);
      return;
    }

    // No longer need modal - checkbox handles this inline
    await performSubmit();
  };

  const performSubmit = async (overrideUseSubset = null) => {
    setLoading(true);

    const finalUseSubset = overrideUseSubset !== null ? overrideUseSubset : useRecipientSubset;

    const formData = new FormData();
    formData.append('email', user.email);
    formData.append('vanity_url', vanityUrl);
    formData.append('subject', mailerSubject);
    formData.append('description', description);
    formData.append('recipients', recipients);
    formData.append('to_recipients', toRecipients);
    formData.append('cc_recipients', ccRecipients);
    formData.append('include_footer', showFooter);
    formData.append('mailer_content', mailerContent);
    formData.append('use_recipient_subset', finalUseSubset.toString());
    if (autoCompleteEnabled && autoCompleteAt) {
      formData.append('auto_complete_at', autoCompleteAt);
      formData.append('auto_complete_message', autoCompleteMessage);
      // Clear completion status if reactivating a completed campaign
      if (campaign?.completion_text && (!campaign?.auto_complete_at || new Date(campaign.auto_complete_at) <= new Date())) {
        formData.append('completion_text', '');
      }
    } else {
      // Send empty values to disable auto-completion
      formData.append('auto_complete_at', '');
      formData.append('auto_complete_message', '');
    }
    console.log('Sending use_recipient_subset:', finalUseSubset);
    let language = undefined;
    if (navigator.languages !== undefined) {
      language = navigator.languages[0];
    } else {
      language = navigator.language;
    }
    formData.append('language', language);
    formData.append('variability', variability / 10);
    formData.append('subject_variability', subjectVariability / 10);
    if (promptInject === 'undefined') {
      formData.append('prompt_inject', '');
    } else {
      formData.append('prompt_inject', promptInject);
    }
    if (tipJarMessage === 'undefined') {
      formData.append('tip_jar_message', '');
    } else {
      formData.append('tip_jar_message', tipJarMessage);
    }
    if (user.active_organization_id) {
      formData.append('organization_id', user.active_organization_id);
    }
    formData.append('typos', typos);
    formData.append('tone', tone);
    formData.append('mp_lookup', mpLookup);
    formData.append('require_sender_address', requireSenderAddress);
    const timeout = 180000;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    // Determine which campaign ID to use: existing campaign only
    const campaignIdToUpdate = campaign?._id;
    const isUpdate = !!campaignIdToUpdate;

    const result = await fetch(`/api/v1/campaign${campaignIdToUpdate ? ('/' + campaignIdToUpdate) : ''}`, {
      method: isUpdate ? 'PUT' : 'POST',
      body: formData,
      signal: controller.signal
    });
    clearTimeout(id);
    const data = await result.json();
    if (data && result.status === 200) {
      // Success... Hide form and show status.
      handleCancelForm(true);
      setBanner(`Campaign ${isUpdate ? 'updated' : 'created'} successfully! ${isUpdate ? 'Updates' : 'Campaign generation'} may take a few moments`);
      return true;
    } else {
      // Failure..
      return false;
    }
  };

  const verifyAllEmails = () => {
    if (emailValidations && Object.keys(emailValidations).length > 0) {
      const invalidEmails = Object.values(emailValidations).filter(validation => validation?.status === 'invalid');
      if (invalidEmails && invalidEmails.length > 0) {
        return false;
      }
    }
    return true;
  };

  const getTotalCharacterCount = () => {
    let count = toRecipients.join(',').length + ccRecipients.join(',').length + recipients.join(',').length + mailerSubject.length + mailerContent.length;

    // Add copyright length if organization has one
    if (user?.active_organization?.copyright_text) {
      const currentYear = new Date().getFullYear();
      const copyrightText = `© ${currentYear} ${user.active_organization.copyright_text}`;
      count += copyrightText.length + 2; // +2 for newlines
    }

    return count;
  };

  const parseEmailFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        let text = e.target.result;
        // First, strip out all single and double quotes
        text = text.replace(/['"]/g, '');
        // Split by commas, spaces, newlines, or carriage returns
        let emails = text.split(/[,\s\n\r]+/).map(email => email.trim());
        // Filter out empty strings and validate basic email format
        emails = emails.filter(email => email && email.includes('@') && email.length > 3);
        resolve(emails);
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const handleFileDrop = async (e, fieldType) => {
    e.preventDefault();
    setDragOver({ to: false, cc: false, bcc: false });

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];
    if (!file.type.includes('text') && !file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      alert('Please drop a text or CSV file');
      return;
    }

    try {
      const emails = await parseEmailFile(file);
      if (emails.length === 0) {
        alert('No valid email addresses found in file');
        return;
      }

      // Add emails directly without ZeroBounce validation (too expensive for bulk uploads)
      // Add emails directly without ZeroBounce validation (too expensive for bulk uploads)
      let newToRecipients = toRecipients;
      let newCcRecipients = ccRecipients;
      let newRecipients = recipients;

      if (fieldType === 'to') {
        // Check if adding these emails would exceed 30 To recipients
        if (toRecipients.length + emails.length > 30) {
          alert(`Cannot add ${emails.length} emails to To: field. Maximum 30 To: recipients allowed. Please use CC: or BCC: fields for additional recipients.`);
          return;
        }
        newToRecipients = [...toRecipients, ...emails];
        setToRecipients(newToRecipients);
      } else if (fieldType === 'cc') {
        newCcRecipients = [...ccRecipients, ...emails];
        setCcRecipients(newCcRecipients);
      } else if (fieldType === 'bcc') {
        newRecipients = [...recipients, ...emails];
        setRecipients(newRecipients);
      }

      // Check if we should warn about sampling or force it
      const totalRecipients = newRecipients.length + newToRecipients.length + newCcRecipients.length;
      const isUpdate = !!campaign?._id;

      if (!isUpdate && totalRecipients >= 100) {
        // Force sampling for 100+ recipients
        setUseRecipientSubset(true);
        alert(`Added ${emails.length} email addresses from file. Since you now have ${totalRecipients} recipients, recipient sampling has been automatically enabled to prevent spam filter issues.`);
      } else if (!isUpdate && totalRecipients > 30) {
        alert(`Added ${emails.length} email addresses from file. Since you now have ${totalRecipients} recipients, you'll be asked about using recipient sampling when you submit the campaign.`);
      } else {
        alert(`Added ${emails.length} email addresses from file`);
      }
    } catch (error) {
      alert('Error reading file: ' + error.message);
    }
  };

  const handleDragOver = (e, fieldType) => {
    e.preventDefault();
    setDragOver(prev => ({ ...prev, [fieldType]: true }));
  };

  const handleDragLeave = (e, fieldType) => {
    e.preventDefault();
    setDragOver(prev => ({ ...prev, [fieldType]: false }));
  };

  const checkCompliance = async () => {
    setFetchingCompliance(true);
    const fd = new FormData();
    fd.append('campaign_text', mailerContent);
    const check = await fetch(`/api/v1/campaigns/compliance`, {method: 'POST'});
    setFetchingCompliance(false);
    if (check.status === 200) {
      const output = await check.json();
      alert(output['compliance']['compliance']);
    } else {
      alert('Unable to determine compliance.');
    }
  };

  const handleCancelForm = (shouldRefresh = false) => {
    cancelForm(shouldRefresh);
  };

  const validateUrl = async () => {
    if (vanityUrl.indexOf(' ') !== -1) {
      setUriErrors('The name of the campaign cant have spaces or illegal characters - instead of space use _');
      return false;
    }
    const regex = /^[a-zA-Z0-9-_]+$/;
    if (!regex.test(vanityUrl)) {
      setUriErrors('The name of the campaign cant have illegal characters - use only numbers, letters, dashes, or underscores');
      return false;
    }

    const check = await fetch(`/api/v1/uri_check?uri=${vanityUrl}`);
    if (check.status === 200) {
      setUriErrors();
      return true;
    } else {
      setUriErrors(`"${vanityUrl}" is already taken. Please choose another campaign name.`);
      return false;
    }
  };

  const handleEmailInputString = (data) => {
    let emailList = [];
    if (data.indexOf(',') !== -1) {
      emailList = data.split(',').filter(e => e.indexOf('@') !== -1).map(t => t.trim().toLowerCase());
    } else if (data.indexOf(' ') !== -1) {
      emailList = data.split(' ').filter(e => e.indexOf('@') !== -1).map(t => t.trim().toLowerCase());
    } else {
      emailList = [data];
    }
    return emailList;
  };

  const removeRecipient = (r) => {
    const newEmailValidations = {...emailValidations};
    delete newEmailValidations[r];
    setEmailValidations(newEmailValidations);
    setRecipients(recipients.filter(f => f !== r));
  }

  const removeToRecipient = (r) => {
    const newEmailValidations = {...emailValidations};
    delete newEmailValidations[r];
    setEmailValidations(newEmailValidations);
    setToRecipients(toRecipients.filter(f => f !== r));
  }

  const removeCcRecipient = (r) => {
    const newEmailValidations = {...emailValidations};
    delete newEmailValidations[r];
    setEmailValidations(newEmailValidations);
    setCcRecipients(ccRecipients.filter(f => f !== r));
  }

  const toggleFooter = () => {
    if (showFooter) {
      setShowFooter(false);
    } else {
      setShowFooter(true);
    }
  }

  const toggleTypos = () => {
    if (typos) {
      setTypos(false);
    } else {
      setTypos(true);
    }
  }

  const toggleMpLookup = () => {
    if (mpLookup) {
      setMpLookup(false);
    } else {
      setMpLookup(true);
    }
  }

  const getEmailValidationColor = (email) => {
    return getValidationColor(email, emailValidations);
  };

  const getEmailStatus = (email) => {
    return getValidationStatus(email, emailValidations);
  };

  const setRecipientsValidated = async (recArray, setter) => {
    setValidationErrors([]);
    setter(recArray);

    // Only validate emails that haven't been validated yet
    const newEmails = recArray.filter(email => needsValidation(email, emailValidations));

    if (newEmails.length > 0) {
      try {
        setValidatingEmails(true);

        let newValidations;

        if (campaign?._id) {
          // Existing campaign - use campaign-based validation
          newValidations = await validateEmails(campaign._id, newEmails);
        } else {
          // New campaign - use standalone validation
          newValidations = await validateEmailsStandalone(newEmails);
        }

        setEmailValidations(prev => ({...prev, ...newValidations}));
        setValidatingEmails(false);
      } catch (error) {
        console.error('Error validating emails:', error);
        setValidatingEmails(false);
        // Continue without validation if API call fails
      }
    }
  }

  const unpaidCampaign = () => {
    if (user?.super_unlimited) {
      return false;
    }
    if (campaign && campaign.limit > 100) {
      return false;
    }
    return true;
  };

  const handleAdvancedSectionChange = (keys) => {
    setAdvancedSectionExpanded(keys);
    // Save the expanded state to cookie (true if 'advanced' key is in the Set)
    setCookie('campaignFormAdvancedExpanded', keys.has('advanced'));
  };

  return (
    <>
      <div className='campaign-form'>
      { showPreview && <CampaignPreview user={user} cancelPreview={() => setShowPreview(false)}
                                        language={(navigator && navigator.language) ? navigator.language : navigator.languages[0]}
                                        setBanner={setBanner}
                                        cancelForm={handleCancelForm}
                                        mailer_content={mailerContent} subject={mailerSubject} tone={tone}
                                        variability={variability} vanity_url={vanityUrl}
                                        subject_variability={subjectVariability}
                                        stompClient={stompClient} typos={typos} footer={showFooter}
                                        campaign={campaign} prompt_inject={promptInject}
                                        tip_jar_message={tipJarMessage}
                                        description={description}
                                        emailValidations={emailValidations}
                                        toRecipients={toRecipients} ccRecipients={ccRecipients} bccRecipients={recipients} />
      }

      <Form validationErrors={validationErrors}
            onSubmit={submitCampaign}
            validationBehavior='aria'>
        <div className='vanity_url'>
          <span style={{marginTop: '5px', fontSize: '15px', fontWeight: 'bold', display: 'flex'}}>https://app.ActivistMailer.com/</span>
          <input className='vanity_url-input'
                 placeholder="my_cause"
                 value={vanityUrl}
                 minLength='4'
                 type="text"
                 variant="bordered"
                 onKeyDown={(d) => {if (d.keyCode === 13) {d.keyCode = 0; d.preventDefault() }}}
                 onChange={(d) => setVanityUrl(d.target.value)}
          />
          <div style={{display: 'flex'}}>
            <Tooltip isOpen={isVanityTooltipOpen} content="Create the name of the link for your campaign">
              <Badge onClick={() => {
                         if (isVanityTooltipOpen) {
                           setIsVanityTooltipOpen(false);
                         } else {
                           setIsVanityTooltipOpen(true);
                         }
                     }}
                     variant='solid' color="primary" content="?" size="sm" />
            </Tooltip>
          </div>
        </div>
        <div className='url-errors'>{uriErrors}</div>
        { loading
          ? <div>
              <Spinner />
              <div style={{width: '480px', display: 'inline-block'}}>
                <span style={{paddingLeft: '6px', fontSize: '14px'}}>
                  Processing. This may take a few minutes.
                </span>
              </div>
            </div>
          :
            ''
        }
        <p />
        <div className='helper'>
          <div className='campaign-form-input'>
            <Input style={{color: '#f0f0f0'}} isRequired label='Initial Subject of Email'
                   onKeyDown={(d) => {if (d.keyCode === 13) {d.keyCode = 0; d.preventDefault() }}}
                   placeholder="Attention please"
                   validationBehavior='aria'
                   fullWidth='true'
                   isInvalid={subjectError ? true : false}
                   errorMessage={subjectError}
                   type="text"
                   value={mailerSubject}
                   color='secondary'
                   variant="bordered"
                   onChange={(d) => {
                     d.preventDefault();
                     setSubjectError();
                     const cleanedValue = replaceUnicodeCharacters(d.target.value);
                     setMailerSubject(cleanedValue);
                   }}
            />
            <div style={{display: 'flex'}}>
              <Tooltip isOpen={isInitialTooltipOpen} content="The initial subject line of the email">
                <Badge onClick={() => {
                         if (isInitialTooltipOpen) {
                           setIsInitialTooltipOpen(false);
                         } else {
                           setIsInitialTooltipOpen(true);
                         }
                       }}
                       variant='solid' color="primary" content="?" size="sm" />
              </Tooltip>
            </div>
          </div>
        </div>

        <div className='helper'>
          <div className='campaign-form-input'>
            <Textarea style={{color: '#202020'}} disableAnimation
                      classNames={{base: 'max-w-[800px]', input: 'resize-y min-h-[40px]'}}
                      variant='bordered'
                      label='Campaign Description'
                      validationBehavior='aria' size="lg"
                      value={description}
                      placeholder="Enter a description for your campaign (optional)"
                      onChange={(d) => setDescription(d.target.value)}
            />
            <div style={{display: 'flex'}}>
              <Tooltip isOpen={isDescriptionTooltipOpen} content="Optional description for your campaign">
                <Badge onClick={() => {
                         if (isDescriptionTooltipOpen) {
                           setIsDescriptionTooltipOpen(false);
                         } else {
                           setIsDescriptionTooltipOpen(true);
                         }
                       }}
                       variant='solid' color="primary" content="?" size="sm" />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className='helper'>
          <div className='error-header'>
            {validationErrors ? validationErrors.map((x) => <div style={{paddingBottom:'8px'}}>{x}</div>) : ''}
          </div>

          { (toRecipients?.length === 0 && recipients?.length ===  0 && ccRecipients?.length === 0) &&
              <div style={{width: '100%', textAlign: 'left', paddingLeft: '8px'}}>
                <span style={{fontSize: '12px'}}>Hit enter after each email address that you want to bcc or deliver to</span>
              </div>
          }

          <div style={{display: 'flex', flexWrap: 'wrap', alignContent: 'start', padding: '6px', width: '100%', maxHeight: '165px', overflowY: 'auto', border: getRecipientBorderColor(toRecipients), borderRadius: '6px', marginBottom: '12px'}}>
            { toRecipients.map(r => <Popover id={`pop_${r}`}>
                                      <PopoverTrigger>
                                        <Chip key={r} color={getEmailValidationColor(r)} variant="bordered"
                                          onClose={(c) => removeToRecipient(r)}>
                                          To: {r}
                                        </Chip>
                                      </PopoverTrigger>
                                      <PopoverContent>
                                         {getEmailStatus(r) || 'Valid email'}
                                         <Button style={{margin: '4px'}} onClick={() => moveEmail(r, setToRecipients, setCcRecipients)}>Move to CC</Button>
                                         <Button style={{margin: '4px'}} onClick={() => moveEmail(r, setToRecipients, setRecipients)}>Move to BCC</Button>
                                      </PopoverContent>
                                    </Popover>
              )
            }
          </div>

          {validatingEmails && (
            <div style={{display: 'flex', alignItems: 'center', padding: '8px', backgroundColor: '#f0f9ff', borderRadius: '6px', marginBottom: '8px'}}>
              <Spinner size="sm" color="primary" />
              <span style={{marginLeft: '8px', color: '#0369a1', fontSize: '14px'}}>
                Validating email addresses...
              </span>
            </div>
          )}

          <div className='campaign-form-input'
               onDrop={(e) => handleFileDrop(e, 'to')}
               onDragOver={(e) => handleDragOver(e, 'to')}
               onDragLeave={(e) => handleDragLeave(e, 'to')}
               style={{
                 border: dragOver.to ? '2px dashed #02A0B0' : 'none',
                 borderRadius: '8px',
                 padding: dragOver.to ? '8px' : '0',
                 backgroundColor: dragOver.to ? 'rgba(2, 160, 176, 0.1)' : 'transparent'
               }}>
            {dragOver.to && (
              <div style={{
                textAlign: 'center',
                padding: '10px',
                color: '#02A0B0',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Drop email file here for To recipients
              </div>
            )}
            <Input style={{marginTop: '8px', color: '#f0f0f0'}} label='To: Email Recipient (or drag & drop file)'
                   type='text'
                   variant='bordered'
                   color='secondary'
                   value={toRecipient}
                   onKeyDown={(d) => {
                     if (d.keyCode === 13) {
                       d.keyCode = 0;
                       d.preventDefault();
                       let newToRecipients = [...toRecipients];
                       const emailsToAdd = handleEmailInputString(d.target.value);

                       // Check if adding these emails would exceed 30 To recipients
                       if (newToRecipients.length + emailsToAdd.length > 30) {
                         alert("Maximum 30 To: recipients allowed. Please use CC: or BCC: fields for additional recipients.");
                         return;
                       }

                       setRecipientsValidated(newToRecipients.concat(emailsToAdd), setToRecipients);
                       setToRecipient('');
                     }
                   }}
                   validationBehavior='aria'
                   placeholder='to_recipient@example.com'
                   onChange={(d) => setToRecipient(d.target.value)}
            />
            <div style={{display: 'flex'}}>
              <Tooltip isOpen={isToEmailTooltipOpen} content="Recipients specified will receive email as normal, visible recipients">
                <Badge onClick={() => {
                         if (isToEmailTooltipOpen) {
                           setIsToEmailTooltipOpen(false);
                         } else {
                           setIsToEmailTooltipOpen(true);
                         }
                       }}
                       variant='solid' color="primary" content="?" size="sm" />
              </Tooltip>
            </div>
          </div>

          <div style={{display: 'flex', flexWrap: 'wrap', alignContent: 'start', padding: '6px', width: '100%', maxHeight: '165px', overflowY: 'auto', border: getRecipientBorderColor(ccRecipients), borderRadius: '6px', marginBottom: '12px'}}>
            { ccRecipients.map(r => <Popover id={`pop_${r}`}>
                                      <PopoverTrigger>
                                        <Chip key={r} color={getEmailValidationColor(r)} variant="bordered"
                                          onClose={(c) => removeCcRecipient(r)}>
                                          Cc: {r}
                                        </Chip>
                                      </PopoverTrigger>
                                      <PopoverContent>
                                         {getEmailStatus(r) || 'Valid email'}
                                         <Button style={{margin: '4px'}} onClick={() => moveEmail(r, setCcRecipients, setToRecipients)}>Move to To</Button>
                                         <Button style={{margin: '4px'}} onClick={() => moveEmail(r, setCcRecipients, setRecipients)}>Move to BCC</Button>
                                      </PopoverContent>
                                    </Popover>
              )
            }
          </div>

          <div className='campaign-form-input'
               onDrop={(e) => handleFileDrop(e, 'cc')}
               onDragOver={(e) => handleDragOver(e, 'cc')}
               onDragLeave={(e) => handleDragLeave(e, 'cc')}
               style={{
                 border: dragOver.cc ? '2px dashed #02A0B0' : 'none',
                 borderRadius: '8px',
                 padding: dragOver.cc ? '8px' : '0',
                 backgroundColor: dragOver.cc ? 'rgba(2, 160, 176, 0.1)' : 'transparent'
               }}>
            {dragOver.cc && (
              <div style={{
                textAlign: 'center',
                padding: '10px',
                color: '#02A0B0',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Drop email file here for CC recipients
              </div>
            )}
            <Input style={{marginTop: '8px', color: '#f0f0f0'}} label='Cc: Email Recipient (or drag & drop file)'
                   type='text'
                   variant='bordered'
                   color='secondary'
                   value={ccRecipient}
                   onKeyDown={(d) => {
                     if (d.keyCode === 13) {
                       d.keyCode = 0;
                       d.preventDefault();
                       let newCcRecipients = [...ccRecipients];
                       setRecipientsValidated(newCcRecipients.concat(handleEmailInputString(d.target.value)), setCcRecipients);
                       setCcRecipient('');
                     }
                   }}
                   validationBehavior='aria'
                   placeholder='cc_recipient@example.com'
                   onChange={(d) => setCcRecipient(d.target.value)}
            />
            <div style={{display: 'flex'}}>
              <Tooltip isOpen={isCcEmailTooltipOpen} content="Recipients specified will receive email as cc">
                <Badge onClick={() => {
                         if (isCcEmailTooltipOpen) {
                           setIsCcEmailTooltipOpen(false);
                         } else {
                           setIsCcEmailTooltipOpen(true);
                         }
                       }}
                       variant='solid' color="primary" content="?" size="sm" />
              </Tooltip>
            </div>
          </div>


          <div style={{display: 'flex', flexWrap: 'wrap', alignContent: 'start', padding: '6px', width: '100%', maxHeight: '165px', overflowY: 'auto', border: getRecipientBorderColor(recipients), borderRadius: '6px', marginBottom: '12px'}}>
            { recipients.map(r => <Popover id={`pop_${r}`}>
                                      <PopoverTrigger>
                                        <Chip key={r} color={getEmailValidationColor(r)} variant="bordered"
                                          onClose={(c) => removeRecipient(r)}>
                                          Bcc: {r}
                                        </Chip>
                                      </PopoverTrigger>
                                      <PopoverContent>
                                         {getEmailStatus(r) || 'Valid email'}
                                         <Button style={{margin: '4px'}} onClick={() => moveEmail(r, setRecipients, setToRecipients)}>Move to To</Button>
                                         <Button style={{margin: '4px'}} onClick={() => moveEmail(r, setRecipients, setCcRecipients)}>Move to CC</Button>
                                      </PopoverContent>
                                    </Popover>
              )
            }
          </div>

          <div className='campaign-form-input'
               onDrop={(e) => handleFileDrop(e, 'bcc')}
               onDragOver={(e) => handleDragOver(e, 'bcc')}
               onDragLeave={(e) => handleDragLeave(e, 'bcc')}
               style={{
                 border: dragOver.bcc ? '2px dashed #02A0B0' : 'none',
                 borderRadius: '8px',
                 padding: dragOver.bcc ? '8px' : '0',
                 backgroundColor: dragOver.bcc ? 'rgba(2, 160, 176, 0.1)' : 'transparent'
               }}>
            {dragOver.bcc && (
              <div style={{
                textAlign: 'center',
                padding: '10px',
                color: '#02A0B0',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Drop email file here for BCC recipients
              </div>
            )}
            <Input style={{marginTop: '8px', color: '#f0f0f0'}} label='Bcc: Email Recipient (or drag & drop file)'
                   type='text'
                   variant='bordered'
                   color='secondary'
                   value={recipient}
                   onKeyDown={(d) => {
                     if (d.keyCode === 13) {
                       d.keyCode = 0;
                       d.preventDefault();
                       let newRecipients = [...recipients];
                       setRecipientsValidated(newRecipients.concat(handleEmailInputString(d.target.value)), setRecipients);
                       setRecipient('');
                     }
                   }}
                   validationBehavior='aria'
                   placeholder='bcc_recipient@example.com'
                   onChange={(d) => setRecipient(d.target.value)}
            />
            <div style={{display: 'flex'}}>
              <Tooltip isOpen={isBccEmailTooltipOpen} content="Recipients specified will receive email as BCC recipients">
                <Badge onClick={() => {
                         if (isBccEmailTooltipOpen) {
                           setIsBccEmailTooltipOpen(false);
                         } else {
                           setIsBccEmailTooltipOpen(true);
                         }
                       }}
                       variant='solid' color="primary" content="?" size="sm" />
              </Tooltip>
            </div>
          </div>

          {/* Recipient Summary Section */}
          {(() => {
            const totalRecipients = recipients.length + toRecipients.length + ccRecipients.length;
            const isOverLimit = totalRecipients > 30;

            return (
              <div style={{
                marginTop: '16px',
                marginBottom: '16px',
                padding: '12px',
                border: isOverLimit ? '2px solid #ff9800' : '1px solid #444',
                borderRadius: '8px',
                backgroundColor: isOverLimit ? 'rgba(255, 152, 0, 0.1)' : 'transparent'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px'
                }}>
                  <div style={{fontSize: '14px', color: '#f0f0f0'}}>
                    <span style={{fontWeight: 'bold'}}>Total Recipients: {totalRecipients}</span>
                    {toRecipients.length > 0 && <span style={{marginLeft: '12px', color: '#aaa'}}>To: {toRecipients.length}</span>}
                    {ccRecipients.length > 0 && <span style={{marginLeft: '12px', color: '#aaa'}}>CC: {ccRecipients.length}</span>}
                    {recipients.length > 0 && <span style={{marginLeft: '12px', color: '#aaa'}}>BCC: {recipients.length}</span>}
                  </div>
                  {isOverLimit && (
                    <span style={{
                      color: '#ff9800',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      ⚠️ Over 30 limit
                    </span>
                  )}
                </div>
                {isOverLimit && (
                  <div style={{marginTop: '8px'}}>
                    <Checkbox
                      isSelected={useRecipientSubset}
                      onValueChange={setUseRecipientSubset}
                      size="sm"
                    >
                      <span style={{fontSize: '14px', color: '#f0f0f0'}}>
                        Send to random subset of 30 recipients
                      </span>
                    </Checkbox>
                    <div style={{
                      fontSize: '12px',
                      color: '#aaa',
                      marginTop: '4px',
                      marginLeft: '24px'
                    }}>
                      Recommended: Helps avoid spam filters and blacklisting
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        <div className='helper'>
          <div className='campaign-form-input'>
            <Textarea style={{color: '#202020'}} disableAnimation
                      classNames={{base: 'max-w-[800px]', input: 'resize-y min-h-[40px]'}}
                      isRequired variant='bordered'
                      label='Initial Email Body'
                      errorMessage={bodyError}
                      isInvalid={bodyError ? true : false}
                      validationBehavior='aria' size="lg"
                      value={mailerContent}
                      onChange={(d) => {
                        setBodyError();
                        const cleanedValue = replaceUnicodeCharacters(d.target.value);
                        setMailerContent(cleanedValue);
                      }}
            />
          </div>
          <div style={{position: 'absolute', top: '0px', right: '0px', marginTop: '-12px'}}>
            <Tooltip isOpen={isBodyTooltipOpen} content="Initial body of email message.">
              <Badge onClick={() => {
                       if (isBodyTooltipOpen) {
                         setIsBodyTooltipOpen(false);
                       } else {
                         setIsBodyTooltipOpen(true);
                       }
                     }}
                     variant='solid' color="primary" content="?" size="sm" />
            </Tooltip>
          </div>
          <div style={{
            fontSize: '12px',
            color: getTotalCharacterCount() > 4096 ? '#ff4444' : '#888888',
            marginTop: '4px',
            textAlign: 'left',
            paddingBottom: '4px'
          }}>
            Character count: {getTotalCharacterCount()} / 4096 (includes recipients, subject, and body)
          </div>
          { user?.admin &&
              <div>
                <Textarea disableAnimation
                          classNames={{
                            base: 'max-w-[800px]',
                            input: 'resize-y min-h-[40px]',
                            inputWrapper: 'border-orange-500 border-2'
                          }}
                          variant='bordered'
                          label='Admin Prompt Inject'
                          value={promptInject}
                          validationBehavior='aria' size="lg"
                          onChange={(d) => setPromptInject(d.target.value)}
                />
                <div style={{marginTop: '12px'}}>
                  <Textarea disableAnimation
                            classNames={{
                              base: 'max-w-[800px]',
                              input: 'resize-y min-h-[40px]',
                              inputWrapper: 'border-orange-500 border-2'
                            }}
                            variant='bordered'
                            label='Tip Jar Message'
                            placeholder='Custom message for the tip jar section (leave blank for default)'
                            value={tipJarMessage}
                            validationBehavior='aria' size="lg"
                            onChange={(d) => setTipJarMessage(d.target.value)}
                  />
                </div>
             </div>
          }
        </div>
        <Accordion
          style={{fontColor: 'white'}}
          selectedKeys={advancedSectionExpanded}
          onSelectionChange={handleAdvancedSectionChange}
        >
          <AccordionItem key='advanced' style={{color: 'white'}} title='Advanced'>
            <div className='slider-container'>
              <div style={{display: 'flex', position: 'relative', width: '100%', top: '8px', left: '138px'}}>
                { subjectVariability > 0.0 &&
                  <Tooltip isOpen={isSubjectVariabilityTooltipOpen} content="How variable should the subject be">
                    <Badge onClick={() => {
                             if (isSubjectVariabilityTooltipOpen) {
                               setIsSubjectVariabilityTooltipOpen(false);
                             } else {
                               setIsSubjectVariabilityTooltipOpen(true);
                             }
                           }}
                           variant='solid' color="primary" content="?" size="sm" />
                  </Tooltip>
                }
              </div>
              <Slider color='warning'
                      className='max-w-md-[700px]'
                      defaultValue={subjectVariability}
                      maxValue={10}
                      minValue={0}
                      showTooltip={true}
                      step={0.1}
                      onChange={(value) => setSubjectVariability(value)}
                      label={`Subject Variability${( subjectVariability === 0 || subjectVariability === 0.0 ) ? ' - Minimal subject changes' : ''}`}
              />
              <div style={{display: 'flex', position: 'relative', width: '100%', top: '8px', left: '122px'}}>
                { variability > 0.0 &&
                  <Tooltip isOpen={isVariabilityTooltipOpen} content="How variable should the body of the message be">
                    <Badge onClick={() => {
                             if (isVariabilityTooltipOpen) {
                               setIsVariabilityTooltipOpen(false);
                             } else {
                               setIsVariabilityTooltipOpen(true);
                             }
                           }}
                           variant='solid' color="primary" content="?" size="sm" />
                  </Tooltip>
                }
              </div>
              <Slider color='warning'
                      className='max-w-md-[700px]'
                      defaultValue={variability}
                      maxValue={10}
                      minValue={0}
                      showTooltip={true}
                      step={0.1}
                      onChange={(value) => {setVariability(value); if (value === 0) { setTypos(false)}}}
                      label={`Body Variability${( variability === 0 || variability === 0.0 ) ? ' - No body changes' : ''}`}
              />
              <div style={{display: 'flex', position: 'relative', width: '100%', top: '8px', left: '47px'}}>
                <Tooltip isOpen={isToneTooltipOpen} content="How variable should the tone be">
                  <Badge onClick={() => {
                           if (isToneTooltipOpen) {
                             setIsToneTooltipOpen(false);
                           } else {
                             setIsToneTooltipOpen(true);
                           }
                         }}
                         variant='solid' color="primary" content="?" size="sm" />
                </Tooltip>
              </div>
              <Slider color='warning'
                      className='max-w-md-[700px]'
                      defaultValue={tone}
                      showTooltip={true}
                      maxValue={10}
                      minValue={0}
                      step={0.1}
                      onChange={(value) => setTone(value)}
                      label="Tone"
              />
            </div>
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <div className='select-container'>
                <div style={{marginTop: '14px'}}>
                  <Checkbox isSelected={typos} onValueChange={toggleTypos}>
                    <span onClick={toggleTypos}>Periodically include commonly typoed words</span>
                  </Checkbox>
                </div>
                <div style={{marginTop: '8px'}}>
                  <Checkbox isDisabled={unpaidCampaign()}
                            isSelected={showFooter}  onValueChange={toggleFooter}>
                    <span onClick={toggleFooter}>Include ActivistMailer Footer</span>
                  </Checkbox>
                </div>
                <div style={{marginTop: '8px'}}>
                  <Checkbox isSelected={mpLookup} onValueChange={toggleMpLookup}>
                    <span onClick={toggleMpLookup}>Use MP Lookup Feature</span>
                  </Checkbox>
                </div>
                {mpLookup && (
                  <div style={{marginTop: '8px', marginLeft: '24px'}}>
                    <Checkbox isSelected={requireSenderAddress} onValueChange={setRequireSenderAddress}>
                      <span onClick={() => setRequireSenderAddress(!requireSenderAddress)}>Require sender physical address</span>
                    </Checkbox>
                  </div>
                )}
                <div style={{marginTop: '8px'}}>
                  <Checkbox isSelected={autoCompleteEnabled} onValueChange={setAutoCompleteEnabled}>
                    <span onClick={() => setAutoCompleteEnabled(!autoCompleteEnabled)}>Auto-complete campaign at specified date/time</span>
                  </Checkbox>
                </div>
                {autoCompleteEnabled && (
                  <div style={{marginTop: '12px', marginLeft: '24px', display: 'flex', flexDirection: 'column', gap: '12px'}}>
                    <div style={{maxWidth: '300px'}}>
                      <label style={{display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#ffffff'}}>
                        End Date/Time
                      </label>
                      <input
                        type="datetime-local"
                        value={autoCompleteAt}
                        onChange={(e) => setAutoCompleteAt(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '2px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          minHeight: '40px',
                          boxSizing: 'border-box',
                          color: '#374151',
                          backgroundColor: '#ffffff'
                        }}
                      />
                    </div>
                    <Textarea
                      label="Completion Message"
                      placeholder="Message to show users who visit the campaign after it ends..."
                      value={autoCompleteMessage}
                      onChange={(e) => setAutoCompleteMessage(e.target.value)}
                      size="sm"
                      minRows={2}
                      maxRows={4}
                    />
                  </div>
                )}
              </div>
            </div>
            <div style={{marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #333'}}>
              <Button onPress={() => checkCompliance()} color='success'>Check 501(c)(3) Compliance { fetchingCompliance && <Spinner size='sm' /> }</Button>
            </div>
          </AccordionItem>
        </Accordion>
        <div style={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap', paddingTop: '8px', minHeight: '70px'}}>
          <div style={{margin: '6px'}}>
            <Button onPress={() => handleCancelForm()}>Cancel</Button>
          </div>
          <div style={{margin: '6px'}}>
            <Button isDisabled={variability === 0 && subjectVariability === 0} onPress={() => setShowPreview(true)} color='success'>Preview</Button>
          </div>
          <div style={{margin: '6px'}}>
            <Button color={verifyAllEmails() ? 'success' : 'warning'} type='submit'>{campaign?._id  ? 'Update' : 'Create'} Campaign</Button>
          </div>
        </div>
      </Form>
    </div>
    </>
  );
}

export default CampaignForm;
